import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import _isEqual from 'lodash/isEqual';
import xdk, { util } from '@accedo/xdk-core';
import cx from 'classnames';
import styles from './playerModal.scss';
import { mainRoutes } from '#/utils/routes';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import {
  playerConstants,
  fullscreenPlayerScreenName,
  ageGroupsKeys,
  initialVideoContainers,
} from '#/config/constants';
import VODPlayer from '#/components/Player/VODPlayer';
import {
  setVideoTime,
  setVideoContent,
  setFullScreenTimeoutRef,
  clearVideoFullscreenBookmark,
  setAdDetailsLoaded,
  setAdPlayed,
  clearPrerollAds,
  setBackgroundPlayerFullscreen,
  setExitFromPlayer,
} from '#/redux/modules/appPlayer/actions';
import {
  getCurrentVideo,
  getBookmarkedVideoContent,
  getFullscreenBookmarkedVideoContent,
  getPrerollAdDuration,
  getAdDetailsLoaded,
  getAdPlayed,
  getBackgroundPlayerFullscreen,
  getVideoStarted,
  getFullScreenTimeoutRef,
} from '#/redux/modules/appPlayer/selectors';

import {
  getCurrentProfileAgeGroup,
  getCurrentProfileId,
} from '#/redux/modules/onboarding/selectors';
import {
  getAppStarted,
  getNextView,
  getDeepLinkStarted,
} from '#/redux/modules/lifecycle/selectors';
import { setNextView, APP_STATUS } from '#/redux/modules/lifecycle/actions';
import {
  getAssetsByReferenceIds,
  getSearchData,
  getAreContainersReady,
  getContainerData,
  getAssetData,
} from '#/redux/modules/brightcove/selectors';
import {
  clearSearchQuery,
  clearSearchData,
  loadAssetsByReferenceIds,
  saveSearchQuery,
  performSearch,
} from '#/redux/modules/brightcove/actions';

import {
  loadCustomDataVideoBookmark,
  clearCustomDataVideoBookmark,
  deleteCustomDataVideoBookmark,
} from '#/redux/modules/identity/actions';

import { sendScreenView } from '#/redux/modules/firestore/actions';
import { getCustomDataVideoBookmark } from '#/redux/modules/identity/selectors';

import useToggleHeader from '#/hooks/useToggleHeader';
import { isWorkstation, isVIZIO } from '#/utils/componentStyleConfig';
import assembleCurrentVideo from '#/utils/assembleCurrentVideo';

import LogoutRedirect from '#/components/LogoutRedirect/LogoutRedirect';

const FULLSCREEN_DELAY = 12000;
const RESUME_END_WINDOW_MS = 45000;

const PlayerModal = props => {
  const dispatch = useDispatch();
  const [showVOD, setShowVOD] = useState(false);
  const currentVideo = useSelector(getCurrentVideo);
  const bookmarkedVideoContent = useSelector(getBookmarkedVideoContent);
  const fullscreenCustomBookmark = useSelector(getCustomDataVideoBookmark);
  const areContainersReady = useSelector(getAreContainersReady);
  const fullscreenBookmarkedVideoContent = useSelector(
    getFullscreenBookmarkedVideoContent,
  );
  const nextView = useSelector(getNextView);
  const hasVideoStarted = useSelector(getVideoStarted);
  const prerollAdDuration = useSelector(getPrerollAdDuration);
  const isAdDetailsLoaded = useSelector(getAdDetailsLoaded);
  const adPlayed = useSelector(getAdPlayed);
  const currentAsset = useSelector(getAssetData);
  const selectedAgeGroup = useSelector(state =>
    getCurrentProfileAgeGroup(state),
  );
  const initialVideo = useSelector(state => {
    let vcId = '';
    switch (selectedAgeGroup) {
      case ageGroupsKeys.TWO_UP:
        vcId = initialVideoContainers.CONTAINER_2Up;
        break;
      case ageGroupsKeys.FIVE_UP:
        vcId = initialVideoContainers.CONTAINER_5Up;
        break;
      default:
        vcId = initialVideoContainers.CONTAINER_8Up;
        break;
    }
    return getContainerData(state, [vcId])[vcId];
  }, _isEqual);
  const fullScreenTimeoutRef = useSelector(getFullScreenTimeoutRef);

  const playerContainer = useRef();
  const lastVideoId = useRef();
  const videoSwitch = useRef(false);
  const isBackgroundPlayerReady = useRef(false);
  const isFullscreenPlayerReady = useRef(false);
  const isFullScreenPlayer = useMemo(
    () => props.location.pathname.startsWith('/player'),
    [props.location.pathname],
  );
  const isBackgroundPlayer = useMemo(
    () => props.location.pathname === mainRoutes.HOME,
    [props.location.pathname],
  );

  // auxiliar render as sometimes isFullscreenPlayerReady is set to true
  // but there won't be another rerender stopping on a white screen.
  const [, setAuxRender] = useState(false);

  const isBackgroundPlayerFullscreen = useSelector(state =>
    getBackgroundPlayerFullscreen(state),
  );

  const toggleHeader = useToggleHeader();

  // Deep linking
  const appStarted = useSelector(getAppStarted);
  const referenceId = props.match?.params?.referenceId;
  const videoAssetByReferenceId = useSelector(getAssetsByReferenceIds);
  const queryData = useSelector(state => getSearchData(state));

  const profileId = useSelector(getCurrentProfileId);
  const isUserNormalLogged = util.isNumber(profileId);
  const deepLinkStarted = useSelector(getDeepLinkStarted);

  // TODO: Still needed?
  // Effects for deep linking, load video assets if referenceId is present in url then set the playback content accordingly.
  useEffect(() => {
    if (appStarted && referenceId) {
      dispatch(loadAssetsByReferenceIds([{ referenceIds: [referenceId] }]));
    }
  }, [appStarted, referenceId]);

  useEffect(() => {
    // Do not run if no referenceId or if currentVideo already has the referenceId information
    if (!referenceId || currentVideo?.videoReferenceId === referenceId) {
      return;
    }

    if (videoAssetByReferenceId?.count) {
      const videoContent = videoAssetByReferenceId.videos[0];
      const { show_id, season_number, age_range } = videoContent?.custom_fields;
      const searchOpts = {
        show_id,
        season_number,
        age: age_range?.charAt(0),
        limit: queryData?.limit,
        page: 0,
        offset: 0,
      };

      dispatch(saveSearchQuery(searchOpts));
      dispatch(performSearch([searchOpts]));
      dispatch(
        setVideoContent({
          currentVideo: assembleCurrentVideo(videoContent),
          playlists: [queryData?.containerId],
          currentPlaylistIndex: 0,
          currentVideoIndex: 0,
        }),
      );
    }
  }, [
    videoAssetByReferenceId,
    queryData?.containerId,
    deepLinkStarted,
    isUserNormalLogged,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearSearchData());
      dispatch(clearSearchQuery());
      dispatch(setBackgroundPlayerFullscreen(false));
    };
  }, []);

  const goFullscreen = () => {
    dispatch(modalActions.closeModal());
    dispatch(
      setNextView({ nextView: APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF }),
    );
    dispatch(setBackgroundPlayerFullscreen(true));
    dispatch(setExitFromPlayer(true));
    toggleHeader(false);
  };

  useEffect(() => {
    if (
      !isWorkstation() &&
      isBackgroundPlayer &&
      showVOD &&
      areContainersReady &&
      hasVideoStarted &&
      nextView === APP_STATUS.NAVIGATION_BG_PLAYER_ON
    ) {
      // Clean the timer after creating another if exists
      fullScreenTimeoutRef ?? clearTimeout(fullScreenTimeoutRef);

      dispatch(setBackgroundPlayerFullscreen(false));
      let timer = setTimeout(goFullscreen, FULLSCREEN_DELAY);
      dispatch(setFullScreenTimeoutRef(timer));

      const resetTimer = () => {
        clearTimeout(timer);
        timer = setTimeout(goFullscreen, FULLSCREEN_DELAY);
        dispatch(setFullScreenTimeoutRef(timer));
      };

      xdk.environment.addEventListener(
        xdk.environment.SYSTEM.KEYDOWN,
        resetTimer,
      );

      return () => {
        clearTimeout(timer);
        // TODO: (rework) find better place for this...
        // dispatch(videoStarted(false));
        xdk.environment.removeEventListener(
          xdk.environment.SYSTEM.KEYDOWN,
          resetTimer,
        );
      };
    }

    if (!isBackgroundPlayer && showVOD && areContainersReady) {
      dispatch(setBackgroundPlayerFullscreen(true));
      dispatch(setExitFromPlayer(true));
    }

    videoSwitch.current = false;
  }, [
    showVOD,
    isBackgroundPlayer,
    areContainersReady,
    hasVideoStarted,
    nextView,
  ]);

  useEffect(() => {
    if (
      nextView === APP_STATUS.NAVIGATION_BG_PLAYER_OFF &&
      fullScreenTimeoutRef
    ) {
      clearTimeout(fullScreenTimeoutRef);
    }
  }, [nextView, fullScreenTimeoutRef]);

  useEffect(() => {}, isBackgroundPlayerFullscreen);

  useEffect(() => {
    if (lastVideoId.current !== currentVideo.videoId) {
      videoSwitch.current = true;
    }

    lastVideoId.current = currentVideo.videoId;
  }, [currentVideo]);

  // Used to make loading faster for slower devices.
  // As soon as the user selects the age group we try to load the video
  // without waiting for the home to be populated with all the data.
  useEffect(() => {
    const initialElement = initialVideo?.[0];
    // Only load first outside a deeplink
    if (!initialElement || deepLinkStarted) {
      return;
    }
    // We only want to do this once
    if (!initialElement.played && !isVIZIO()) {
      initialElement.played = true;
      dispatch(
        setVideoContent({
          currentVideo: assembleCurrentVideo(initialElement),
          playlists: [],
          currentPlaylistIndex: 0,
          currentVideoIndex: 0,
        }),
      );
    }
  }, [selectedAgeGroup]);

  // TV background player
  useEffect(() => {
    if (
      !isWorkstation() &&
      isBackgroundPlayer &&
      showVOD &&
      !isBackgroundPlayerReady.current
    ) {
      if (
        fullscreenBookmarkedVideoContent?.currentVideo?.videoId ===
        currentVideo.videoId
      ) {
        fullscreenBookmarkedVideoContent.currentVideo.time = Math.max(
          currentVideo.time,
          fullscreenBookmarkedVideoContent.currentVideo.time,
        );
        dispatch(setVideoContent(fullscreenBookmarkedVideoContent));
      }
      isBackgroundPlayerReady.current = true;
    }
  }, [
    isBackgroundPlayer,
    fullscreenBookmarkedVideoContent,
    showVOD,
    currentVideo,
  ]);

  useEffect(() => {
    if (isBackgroundPlayer && showVOD && !isBackgroundPlayerReady.current) {
      if (bookmarkedVideoContent) {
        if (
          currentVideo.videoId === bookmarkedVideoContent.currentVideo.videoId
        ) {
          // Use case is bookmark is set when going to any other page away from home
          // except for the live player, which sets the current video time on the video content.
          // Avoids issue of overriding BG player bookmark when coming to Live TV from a FS player.
          // i.e. Home -> explore -> fullscreen (changes video content) -> exit -> Live TV
          // User will see video where they left off regardless of which page they go to.
          bookmarkedVideoContent.currentVideo.time = Math.max(
            currentVideo.time,
            bookmarkedVideoContent.currentVideo.time,
          );
        }
        dispatch(setVideoContent(bookmarkedVideoContent));
      }
      isBackgroundPlayerReady.current = true;
    }
  }, [isBackgroundPlayer, bookmarkedVideoContent, showVOD]);

  useEffect(() => {
    if (isFullScreenPlayer && showVOD && !isFullscreenPlayerReady.current) {
      if (
        fullscreenBookmarkedVideoContent &&
        !props.location.state?.fromDetails
      ) {
        if (
          currentVideo.videoId !==
          fullscreenBookmarkedVideoContent.currentVideo.videoId
        ) {
          dispatch(setVideoContent(fullscreenBookmarkedVideoContent));
        }
        dispatch(clearVideoFullscreenBookmark());
      } else {
        setAuxRender(prev => !prev);
      }

      isFullscreenPlayerReady.current = true;
    }
  }, [isFullScreenPlayer, fullscreenBookmarkedVideoContent, showVOD]);

  useEffect(() => {
    const currentPath = props.location.pathname.toLowerCase();
    switch (true) {
      case currentPath === mainRoutes.HOME:
        toggleHeader(true);
        isFullscreenPlayerReady.current = false;
        setShowVOD(true);
        dispatch(setNextView({ nextView: APP_STATUS.NAVIGATION_BG_PLAYER_ON }));
        break;
      case currentPath.startsWith('/player'):
        dispatch(sendScreenView(fullscreenPlayerScreenName));
        dispatch(
          setNextView({ nextView: APP_STATUS.PLAYER_TITLE_ON_CONTROLS_OFF }),
        );
        isBackgroundPlayerReady.current = false;
        xdk.media?.deinit();
        setShowVOD(true);
        break;
      case currentPath === mainRoutes.LIVETV:
        xdk.media?.deinit();
        setShowVOD(false);
        break;
      default:
        dispatch(
          setNextView({ nextView: APP_STATUS.NAVIGATION_BG_PLAYER_OFF }),
        );
        isFullscreenPlayerReady.current = false;
        if (
          xdk.media?.getState() !== 'paused' &&
          xdk.media?.getState() !== 'stopped'
        ) {
          xdk.media?.pause();
        }
        setShowVOD(false);
        break;
    }
  }, [props.location]);

  useEffect(() => {
    if (!playerConstants.CUSTOM_BOOKMARKING_ENABLED) {
      return;
    }
    if (
      currentVideo.videoReferenceId &&
      (!fullscreenBookmarkedVideoContent || props.location.state?.fromDetails)
    ) {
      // If the video has changed into fullscreen and we aren't resuming fullscreen content
      dispatch(loadCustomDataVideoBookmark());
      dispatch(setAdDetailsLoaded(false));
      dispatch(setAdPlayed(false));
      dispatch(clearPrerollAds());
    }
  }, [
    currentVideo.videoId,
    fullscreenBookmarkedVideoContent,
    isFullScreenPlayer,
  ]);

  useEffect(() => {
    if (!playerConstants.CUSTOM_BOOKMARKING_ENABLED) {
      return;
    }
    if (
      isFullScreenPlayer &&
      fullscreenCustomBookmark?.videoId === currentVideo.videoId &&
      isAdDetailsLoaded &&
      adPlayed
    ) {
      const bookmarkProgress = Number(fullscreenCustomBookmark.progress) || 0;
      const videoDuration =
        Number(currentVideo.duration) || Number(currentAsset?.duration) || 0;
      const remainingTimeMs = videoDuration - bookmarkProgress;
      const shouldResetBookmark =
        videoDuration > 0 && remainingTimeMs <= RESUME_END_WINDOW_MS;

      if (shouldResetBookmark) {
        if (currentVideo.videoReferenceId) {
          dispatch(
            deleteCustomDataVideoBookmark(currentVideo.videoReferenceId),
          );
        }
        dispatch(setVideoTime(0));
        dispatch(clearCustomDataVideoBookmark());
        return;
      }

      // custom data bookmark is in milliseconds
      dispatch(setVideoTime((prerollAdDuration + bookmarkProgress) / 1000));
      dispatch(clearCustomDataVideoBookmark());
    }
  }, [
    fullscreenCustomBookmark,
    prerollAdDuration,
    isAdDetailsLoaded,
    adPlayed,
    currentVideo.duration,
    currentVideo.videoReferenceId,
    currentAsset?.duration,
  ]);

  const currentPath = props.location.pathname.toLowerCase();

  const tvPropsOverride = {
    isFullScreenPlayer: true,
    fromHome: isBackgroundPlayer,
    withUI:
      currentPath === mainRoutes.HOME ||
      isBackgroundPlayerFullscreen ||
      isFullScreenPlayer,
  };

  useEffect(() => {
    if (!showVOD) {
      dispatch(setBackgroundPlayerFullscreen(false));
    }
  }, [showVOD]);

  return (
    <>
      <LogoutRedirect />
      <div className={styles.container}>
        <div
          ref={playerContainer}
          className={cx(styles.playerWrapper, {
            [styles.backgroundPlayer]: isBackgroundPlayerFullscreen,
          })}
        >
          {showVOD &&
            ((isFullScreenPlayer && isFullscreenPlayerReady.current) ||
              isBackgroundPlayer) && (
              <VODPlayer
                videoId={currentVideo.videoId}
                videoTitle={currentVideo.videoTitle}
                videoTime={currentVideo.time || 0}
                videoTextTracks={currentVideo.text_tracks}
                isFullScreenPlayer={isFullScreenPlayer}
                container={playerContainer.current}
                {...(!isWorkstation() && tvPropsOverride)}
                isBackgroundPlayerFullscreen={isBackgroundPlayerFullscreen}
                history={props.history}
              />
            )}
        </div>
        <div className={cx({ [styles.hide]: isBackgroundPlayerFullscreen })}>
          {props.children}
        </div>
      </div>
    </>
  );
};

PlayerModal.propTypes = {
  videoId: PropTypes.number,
  children: PropTypes.any,
  location: PropTypes.any,
  match: PropTypes.object,
  history: PropTypes.object,
};

export default withRouter(PlayerModal);
